import { z } from 'zod';
import { Iso, Uuid } from './primitives';

/** Recognize pasted ASINs without treating ordinary ten-letter words as IDs. */
export function researchSearchAsin(value: string | undefined): string | null {
  const normalized = value?.trim().toUpperCase() ?? '';
  return /^[A-Z0-9]{10}$/.test(normalized) && /\d/.test(normalized) ? normalized : null;
}

const number = z.number().finite();
const unknownNumber = number.nullable().default(null);
export const PlanningCandidateSource = z.enum(['reorder_rec', 'sourcing_rec']);
export const PlanningCandidateStatus = z.enum(['pending', 'done', 'deferred', 'excluded']);
export const PlanningEconomics = z.object({
  goodsCents: unknownNumber,
  investedCents: unknownNumber,
  profitCents: unknownNumber,
  profitPerListingCents: unknownNumber,
  projectedSellThroughMonths: unknownNumber,
  profitPerDollarPerMonth: unknownNumber,
});
export const PlanningBuyingProposal = z.object({
  id: z.string(),
  source: PlanningCandidateSource,
  isFirstBuy: z.boolean(),
  velocityBasis: z.enum(['observed', 'estimated', 'unknown']),
  vendorUnitCostCents: unknownNumber,
  vendorUnitsPerListing: number,
  pricePerListingCents: unknownNumber,
  liveFeePerListingCents: unknownNumber,
  prepPerListingCents: unknownNumber,
  monthlySalesListingUnits: unknownNumber,
  ownedVendorUnits: number,
  inboundVendorUnits: number,
  orderMultipleVendorUnits: number,
  minimumOrderVendorUnits: number,
  quantityVendorUnits: number,
  allocatedFreightCents: number,
  economics: PlanningEconomics,
  reason: z.string().nullable(),
  // 2026-09-05: neither the weekly budget nor the ceiling caps selection anymore
  // (see buying-math.ts); this flags where a selected line falls against the
  // (now advisory) weekly budget, ranked-cumulative.
  // Defaulted, not required: PlanningCandidateDecision is a union, and a run
  // persisted before 2026-09-05 has no such key — without the default zod falls
  // through to PlanningBuyingCandidate and strips the whole proposal (quantity,
  // economics, reason) out of an old run's page.
  beyondWeeklyBudget: z.boolean().default(false),
});
/**
 * The pre-publication buying evidence persisted by the nightly worker. It is
 * intentionally separate from a BuyingProposal: quantity/freight/economics
 * are only known after the publisher assembles the whole order.
 */
export const PlanningBuyingCandidate = z.object({
  id: z.string(),
  source: PlanningCandidateSource,
  isFirstBuy: z.boolean(),
  velocityBasis: z.enum(['observed', 'estimated', 'unknown']),
  vendorUnitCostCents: unknownNumber,
  vendorUnitsPerListing: number,
  pricePerListingCents: unknownNumber,
  liveFeePerListingCents: unknownNumber,
  prepPerListingCents: unknownNumber,
  monthlySalesListingUnits: unknownNumber,
  ownedVendorUnits: number,
  inboundVendorUnits: number,
  orderMultipleVendorUnits: number,
  minimumOrderVendorUnits: number,
});
export const PlanningCandidateDecision = z.union([PlanningBuyingProposal, PlanningBuyingCandidate]);
export const PlanningBuyingEvidence = z.object({
  rank: z.number().int().positive(),
  proposal: PlanningBuyingProposal,
  supplierSource: z.object({
    rowId: Uuid,
    sheetId: Uuid,
    supplierId: Uuid,
    parsedAt: Iso.nullable(),
    vendorItemNo: z.string().nullable(),
    upc: z.string().nullable(),
    asin: z.string().nullable(),
    listUnitCostCents: unknownNumber,
    casePack: unknownNumber,
    orderIncrement: unknownNumber,
    minimumOrderVendorUnits: unknownNumber,
  }),
  scenarios: z.array(
    z.object({
      discountPct: number,
      vendorUnitCostCents: unknownNumber,
      economics: PlanningEconomics,
    })
  ),
});
export type PlanningBuyingEvidence = z.infer<typeof PlanningBuyingEvidence>;
export const PlanningRunSummary = z.object({
  status: z.string().nullable().default(null),
  reason: z.string().nullable().default(null),
  orderedBudgetCents: unknownNumber,
  goodsCents: unknownNumber,
  reservedGoodsCents: unknownNumber,
  totalGoodsCents: unknownNumber,
  reservedFreightCents: unknownNumber,
  remainingGoodsBudgetCents: unknownNumber,
  expectedReceivedGoodsCents: unknownNumber,
  allocatedFreightCents: unknownNumber,
  // 2026-09-05: neither the weekly budget nor the ceiling gates selection anymore
  // (see buying-math.ts); these report the consequence instead, so the UI can
  // flag "raise the ceiling" / "over the weekly budget" rather than the run
  // silently returning nothing or truncating a profitable order.
  overBudgetCents: unknownNumber,
  projectedCommittedCents: unknownNumber,
  overCeilingCents: unknownNumber,
  /** Per-SKU inventory hold (2026-09-10 design doc) — count of this plan's
   *  lines currently carrying a non-null hold_reason (regardless of
   *  whether they were held by this publish or a prior one). `status`
   *  reads 'published' rather than 'ready' when this is > 0. */
  heldLines: unknownNumber,
});
export const PlanningRunImpact = z.object({
  proposedLineCount: z.number().int().nonnegative(),
  proposedVendorUnits: z.number().int().nonnegative(),
  proposedGoodsCents: z.number().int().nonnegative(),
  /** This run's proposed lines with hold_reason set — already excluded
   *  from proposedLineCount/proposedVendorUnits/proposedGoodsCents above
   *  (2026-09-10 per-SKU inventory hold design doc); surfaced so the web
   *  can report the count instead of silently dropping them. `.default(0)`
   *  covers a fixture/response captured before this field existed. */
  heldLineCount: z.number().int().nonnegative().default(0),
});
export const PlanningRun = z.object({
  id: Uuid,
  day: z.string(),
  planId: Uuid.nullable(),
  status: z.enum(['pending', 'researching', 'publishing', 'complete', 'held', 'failed']),
  createdAt: Iso,
  updatedAt: Iso,
  finishedAt: Iso.nullable(),
  nextAttemptAt: Iso.nullable(),
  summary: PlanningRunSummary,
  impact: PlanningRunImpact,
  counts: z.array(
    z.object({
      source: PlanningCandidateSource,
      status: PlanningCandidateStatus,
      stage: z.string().nullable(),
      count: z.number().int().nonnegative(),
    })
  ),
});
export type PlanningRun = z.infer<typeof PlanningRun>;
export const LatestPlanningRunResponse = PlanningRun.nullable();
export const PlanningCandidate = z.object({
  id: Uuid,
  key: z.string(),
  source: PlanningCandidateSource,
  status: PlanningCandidateStatus,
  description: z.string().nullable(),
  asin: z.string().nullable(),
  upc: z.string().nullable(),
  itemNo: z.string().nullable(),
  supplierId: Uuid.nullable(),
  reason: z.string().nullable(),
  stage: z.string().nullable(),
  decision: PlanningCandidateDecision.nullable(),
});
export type PlanningCandidate = z.infer<typeof PlanningCandidate>;
export const ListPlanningCandidatesResponse = z.object({
  candidates: z.array(PlanningCandidate),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type ListPlanningCandidatesResponse = z.infer<typeof ListPlanningCandidatesResponse>;

// ─── GET /:runId/research (Faraz 2026-09-06: procurement research page) ──────
//
// A flat, sortable/filterable view over one run's RESEARCHED candidates (the
// ones that made it through screening into Keepa/SP-API research), so the
// procurement surface can show "what did the run find" before order
// planning happens. Distinct from PlanningCandidate above: that endpoint
// paginates every candidate row (including pre-research screen drops) for a
// "why wasn't this picked" drill-down; this one is pre-filtered to rows
// that HAVE a research verdict and adds the research-specific fields
// (anchor price/source, margin, sales rank) the drill-down doesn't expose.

/**
 * Mirrors api.ts's OrderPlanLineVerdictTier string values exactly, but is
 * declared fresh here rather than imported. api.ts already imports
 * PlanningBuyingEvidence from this file (one-directional today); the
 * package's only export surface is index.ts's barrel, which re-exports
 * './api' before './planning-run', so api.ts is always the first of the
 * two to start evaluating. Importing api.ts's enum back into this file
 * would make the dependency circular, and since z.enum(...) runs at module
 * top level (not lazily), planning-run.ts would sometimes observe an
 * undefined export from a still-initializing api.ts. Duplicating the five
 * literal strings sidesteps that entirely — cheaper than reworking two
 * files' load order for one shared enum.
 */
export const PlanningResearchTier = z.enum([
  'optimal',
  'below-target',
  'below-floor',
  'underwater',
  'unknown',
]);
export type PlanningResearchTier = z.infer<typeof PlanningResearchTier>;

/** Same duplication rationale as PlanningResearchTier above — mirrors
 *  api.ts's OrderPlanLineAnchorSource values without importing it.
 *  'lowest_fba_offer' added 2026-09-11 anchor-spread design — see api.ts's
 *  OrderPlanLineAnchorSource doc. */
export const PlanningResearchAnchorSource = z.enum([
  'buy_box',
  'lowest_fba_offer',
  'lowest_offer',
  'intended',
  'listed',
  /** Same duplication rationale — mirrors api.ts's OrderPlanLineAnchorSource
   *  'keepa_price' (2026-09-20 zero-price defect fix). */
  'keepa_price',
]);
export type PlanningResearchAnchorSource = z.infer<typeof PlanningResearchAnchorSource>;

/** Same duplication rationale as PlanningResearchAnchorSource above —
 *  mirrors api.ts's OrderPlanLineAnchorSpread without importing it
 *  (2026-09-11 anchor-spread design). */
export const PlanningResearchAnchorSpread = z.object({
  buyBoxCents: z.number().int(),
  lowestFbaCents: z.number().int(),
  pct: z.number(),
});
export type PlanningResearchAnchorSpread = z.infer<typeof PlanningResearchAnchorSpread>;

// ─── Gating verdict (Faraz 2026-09-06: "Amazon is gating human digestible
// categories from us") ────────────────────────────────────────────────────
//
// Every researched product must carry Amazon's gating verdict for this
// seller account. 'unchecked' is a client-side-only state (the DB never
// writes it) meaning `result.gating` is absent — the run's gating sweep
// (lib/order-planning/gating-sweep.ts) hasn't reached this candidate yet,
// or no SP-API BYOK connection is configured. See PlanningGatingStatus in
// gating-sweep.ts for the other five states, mirrored here rather than
// imported for the same circular-import reason as PlanningResearchTier
// above (api.ts doesn't import gating-sweep.ts either, but the package
// barrel's load order rule is the same).
export const PlanningGatingStatus = z.enum([
  'open',
  'approval_required',
  'not_eligible',
  'asin_not_found',
  'gated',
  'unchecked',
]);
export type PlanningGatingStatus = z.infer<typeof PlanningGatingStatus>;

/** Gate-kind feature (Faraz 2026-09-06: "we cannot tell a brand gate from a
 *  category gate") — mirrors gating-sweep.ts's PlanningGateKind, same
 *  duplication rationale as PlanningResearchTier above. 'unknown' covers
 *  both "no message matched either pattern" and "not gated at all". */
export const PlanningGateKind = z.enum(['brand', 'category', 'unknown']);
export type PlanningGateKind = z.infer<typeof PlanningGateKind>;

export const PlanningResearchGating = z.object({
  status: PlanningGatingStatus,
  reasons: z.array(z.string()),
  /** `.default([])` covers every row gated before this shipped — the DB
   *  column simply has no `messages` key yet. */
  messages: z.array(z.string()).default([]),
  /** `.default('unknown')` for the same pre-existing-row reason as
   *  `messages` above. */
  gateKind: PlanningGateKind.default('unknown'),
  applyUrl: z.string().nullable(),
  checkedAt: z.string().nullable(),
});
export type PlanningResearchGating = z.infer<typeof PlanningResearchGating>;

const UNCHECKED_GATING: PlanningResearchGating = {
  status: 'unchecked',
  reasons: [],
  messages: [],
  gateKind: 'unknown',
  applyUrl: null,
  checkedAt: null,
};

// ─── ASIN identity verdict (Faraz 2026-09-06: "I am having issues finding
// the right ASIN. A lot of product links take me to some random auto
// parts.") ────────────────────────────────────────────────────────────────
//
// screen-policy.ts's screenCatalogCandidate now runs checkAsinIdentity
// (lib/sourcing/identity-check.ts) against every UPC match before trusting
// it, dropping a mismatch as `asin_mismatch` — but that only protects rows
// SCREENED after the fix landed. This surfaces the same verdict on the
// research page for every row, including ones screened (or never screened
// at all) before the fix, so a bad-ASIN row researched under the old code
// path is visibly flagged rather than silently priced as the wrong product.
// 'unverified' is distinct from a passing 'match': it means there is no
// Amazon title/brand evidence at all to compare (never checked, or checked
// before the identity feature existed) — never conflated with an actual
// pass.
export const PlanningResearchIdentity = z.enum(['match', 'mismatch', 'unverified']);
export type PlanningResearchIdentity = z.infer<typeof PlanningResearchIdentity>;

export const PlanningResearchAmazon = z.object({
  /** What Amazon's catalogue actually says this ASIN is — from the screen's
   *  own evidence when the row was screened after the identity-check fix,
   *  otherwise from the shared UPC->ASIN cache (catalog_upc_resolutions). */
  title: z.string().nullable(),
  brand: z.string().nullable(),
  displayGroup: z.string().nullable(),
  identity: PlanningResearchIdentity,
  /** checkAsinIdentity's own reason code ('department_mismatch' |
   *  'no_shared_tokens'), null for 'match'/'unverified'. */
  identityReason: z.string().nullable(),
});
export type PlanningResearchAmazon = z.infer<typeof PlanningResearchAmazon>;

const UNVERIFIED_AMAZON: PlanningResearchAmazon = {
  title: null,
  brand: null,
  displayGroup: null,
  identity: 'unverified',
  identityReason: null,
};

// ─── Pack size verdict (Faraz 2026-09-07: "A lot of optimals are
// multipacks. Once we calculate the pack correctly they probably drop out
// of optimal. Why don't we look into the Amazon listing title, it says the
// pack quantity.") ───────────────────────────────────────────────────────
//
// screen-policy.ts's deriveVendorUnitsPerListing (lib/sourcing/pack-size.ts)
// writes amazonPackQuantity/amazonPackSource/supplierInnerPack/
// vendorUnitsPerListing/packConfidence onto `input.screenEvidence` for
// every candidate it screens; the route builds this block from those
// fields (supplierInnerPack itself stays internal — not surfaced here).
export const PlanningPackConfidence = z.enum(['known', 'assumed_single', 'unclear']);
export type PlanningPackConfidence = z.infer<typeof PlanningPackConfidence>;

// 'override' (2026-09-08 pack-override design) — candidate-inputs.ts stamps
// this onto screenEvidence.amazonPackSource when a human correction
// (sourcing_pack_overrides) won over the detected pack for this candidate.
export const PlanningPackSource = z.enum(['package_quantity', 'size', 'title', 'override']);
export type PlanningPackSource = z.infer<typeof PlanningPackSource>;

export const PlanningResearchPack = z.object({
  vendorUnitsPerListing: z.number().int().nullable(),
  confidence: PlanningPackConfidence,
  amazonPackQuantity: z.number().int().nullable(),
  source: PlanningPackSource.nullable(),
  /** Pack override design (2026-09-08, Faraz: "a text box to enter right
   *  pack quantity and solve it permanently") — the tenant's current
   *  sourcing_pack_overrides row for this ASIN, or null when none is set.
   *  Read live off the overrides table, independent of whatever pack this
   *  particular run's screen actually used (a run researched before the
   *  override was entered still reports the override here — it's the
   *  CURRENT correction, not what was applied at screen time). */
  override: z.number().int().nullable(),
  /** True when anchorPriceCents >= 4x costCents (both positive) AND no
   *  override is set — the same multipack-mismatch signature
   *  packMismatchSuspected already flags, but suppressed once a human has
   *  corrected the pack for this ASIN. Drives the "Verify pack" chip and
   *  filter option. */
  suspect: z.boolean(),
});
export type PlanningResearchPack = z.infer<typeof PlanningResearchPack>;

/** `.default(...)` covers a row screened before this shipped, or evidence
 *  that's missing/malformed — reports 'assumed_single' (research's own ×1
 *  fallback) with every number null, same defaulting convention as
 *  `gating`/`amazon` below. */
const DEFAULT_PACK: PlanningResearchPack = {
  vendorUnitsPerListing: null,
  confidence: 'assumed_single',
  amazonPackQuantity: null,
  source: null,
  override: null,
  suspect: false,
};

// ─── Discount-buckets verdict (Faraz 2026-09-07: "Ideally we should get 15%
// from KeHE but they only give it when we purchase more. Right now we get
// 6%. Can we add the discount buckets 5, 10, 15 and then show
// viability?") ─────────────────────────────────────────────────────────
//
// A row's stored margin (estMarginCents/anchorPriceCents/costCents) is
// priced at whatever supplier cost the run actually researched at (list,
// today). floorAtPct/targetAtPct answer "how big a discount would it take"
// against that same row's own thresholds.minFrac/targetFrac — the smallest
// of buying-math.ts's supplierCostScenarios buckets ([0, 5, 10, 15]) whose
// margin fraction clears each threshold, or null when none of them do (or
// the row's margin/cost/threshold isn't known). marginPctAt reports the
// margin percent at each non-zero bucket regardless of whether it clears
// anything, for the column's tooltip.
export const PlanningResearchDiscount = z.object({
  floorAtPct: z.number().int().nullable(),
  targetAtPct: z.number().int().nullable(),
  marginPctAt: z.object({
    '5': z.number().nullable(),
    '10': z.number().nullable(),
    '15': z.number().nullable(),
  }),
});
export type PlanningResearchDiscount = z.infer<typeof PlanningResearchDiscount>;

/** `.default(...)` covers a row researched (or a client build) before this
 *  feature shipped — reports "never clears" rather than failing to parse,
 *  same convention as `pack`/`gating`/`amazon` above. */
const DEFAULT_DISCOUNT: PlanningResearchDiscount = {
  floorAtPct: null,
  targetAtPct: null,
  marginPctAt: { '5': null, '10': null, '15': null },
};

// ─── Velocity floor (Faraz 2026-09-08 design: "velocity floor 15 units a
// month, 8 to 15 marginal") ─────────────────────────────────────────────
//
// `buyable` today is margin-only — a sourcing candidate carries no per-ASIN
// monthly-units estimate at all. demand-probe.ts's background probe fills
// that gap for ASINs seen in order-planning research runs; candidate-inputs.ts
// stamps the estimate/source/tier onto `input.screenEvidence.velocity` for
// every viable sourcing candidate a probe covers, and
// routes/planning-runs.ts's researchVelocity* SQL (next to researchRankSql)
// reads it back for this block. `tier` mirrors lib/sourcing/velocity.ts's
// tierVelocity exactly: 'unknown' when never probed (or nobody probed this
// candidate's ASIN yet).
export const PlanningVelocityTier = z.enum(['pass', 'marginal', 'fail', 'unknown']);
export type PlanningVelocityTier = z.infer<typeof PlanningVelocityTier>;

export const PlanningResearchVelocity = z.object({
  estimate: z.number().int().nullable(),
  source: z.enum(['badge', 'rank_drops']).nullable(),
  tier: PlanningVelocityTier,
});
export type PlanningResearchVelocity = z.infer<typeof PlanningResearchVelocity>;

/** `.default(...)` covers a row researched before this shipped, or a
 *  reorder_rec row (out of scope for the demand probe — reorder candidates
 *  have real sales history) — reports 'unknown' rather than failing to
 *  parse, same convention as `pack`/`gating`/`discount` above. */
const DEFAULT_VELOCITY: PlanningResearchVelocity = {
  estimate: null,
  source: null,
  tier: 'unknown',
};

// ─── Price context / 90-day Buy Box average (Faraz 2026-09-10: "Are we
// storing both Amazon and Keepa API response payload? ... is it possible to
// get average bb price for last 90 days?") ────────────────────────────────
//
// bbAvg90Cents/bbAvg30Cents come from Keepa `stats.avg90/avg30[18]`
// (index 18 = BUY_BOX_SHIPPING), captured by demand-probe.ts's
// probeTenantDemand onto catalog_asin_demand_probes; routes/planning-runs.ts
// reads them live by the row's resolved ASIN, same live-lookup pattern as
// the velocity block above. anchorVsAvg90Pct = (anchor / bbAvg90 − 1) × 100,
// null when either side is missing (or bbAvg90 isn't positive).
export const PlanningResearchPriceContext = z.object({
  bbAvg90Cents: z.number().int().nullable(),
  bbAvg30Cents: z.number().int().nullable(),
  anchorVsAvg90Pct: z.number().nullable(),
});
export type PlanningResearchPriceContext = z.infer<typeof PlanningResearchPriceContext>;

/** `.default(...)` covers a row researched before this shipped, or an ASIN
 *  the demand probe hasn't reached yet — reports "no context" rather than
 *  failing to parse, same convention as `pack`/`gating`/`velocity` above. */
const DEFAULT_PRICE_CONTEXT: PlanningResearchPriceContext = {
  bbAvg90Cents: null,
  bbAvg30Cents: null,
  anchorVsAvg90Pct: null,
};

/** Row-level warning flags (Faraz 2026-09-10 design) — `price_spike`: the
 *  anchor price is more than 20% above the 90-day Buy Box average.
 *  `amazon` (amazon-retail-exclusion design, same day): Amazon itself sells
 *  this ASIN — see `amazonRetail` below for the full verdict. `price_unstable`
 *  (2026-09-11 anchor-spread design): the row's anchor came from the
 *  lowest-FBA-offer spread rule (anchorSource 'lowest_fba_offer') rather
 *  than the Buy Box itself — a rotating box gave an unreliable price this
 *  run. Surfaced, never hidden — same "flag, don't drop" convention as
 *  packMismatchSuspected (amazon-retail rows are the one exception: they
 *  ARE dropped from `buyable`, see PlanningResearchAmazonRetailCounts). */
export const PlanningResearchFlag = z.enum(['price_spike', 'amazon', 'price_unstable']);
export type PlanningResearchFlag = z.infer<typeof PlanningResearchFlag>;

// ─── Amazon-retail exclusion (Faraz 2026-09-10 design: "no way we can
// compete with them") ────────────────────────────────────────────────────
//
// Mirrors api.ts's AmazonRetailStatus/AmazonRetailVerdict exactly — same
// duplication rationale as PlanningResearchTier's own doc comment above:
// api.ts already imports PlanningBuyingEvidence FROM this file, so this
// file importing api.ts's schema back would make the dependency circular
// (and, unlike a type-only import, break at module-eval time since these
// are zod schema VALUES). Computed live off this row's resolved ASIN, same
// live-lookup-by-ASIN pattern as `velocity`/`priceContext` above.
// `buyable`/`buyableAt`/`buyableProven` below all exclude status 'amazon';
// the row keeps its own margin tier regardless (flagged, not hidden).
export const PlanningAmazonRetailStatus = z.enum(['amazon', 'clear', 'unknown']);
export type PlanningAmazonRetailStatus = z.infer<typeof PlanningAmazonRetailStatus>;
export const PlanningAmazonRetailVerdict = z.object({
  status: PlanningAmazonRetailStatus,
  reasons: z.array(z.string()),
});
export type PlanningAmazonRetailVerdict = z.infer<typeof PlanningAmazonRetailVerdict>;
const DEFAULT_AMAZON_RETAIL: PlanningAmazonRetailVerdict = { status: 'unknown', reasons: [] };

// ─── Research verdict (2026-09-11 research-verdict-simplification design:
// "We made research way complicated ... We have a list. We find with compare
// with Amazon and Keepa and find the products.") ───────────────────────────
//
// One bucket + one reason per row, replacing the page's old sprawl of
// independent filters (tier/gating/identity/pack/velocity/flags/amazonRetail)
// with a single actionable verdict. `reason` is a CLOSED enum of the 13 rule
// outcomes in the design doc's table — order matters there: the first
// matching rule wins, so a row always resolves to exactly one reason.
// deriveResearchVerdict (lib/order-planning/research-verdict.ts) is the pure
// function that implements the table; this schema only carries its output.
export const PlanningVerdictBucket = z.enum(['buy', 'fix', 'no']);
export type PlanningVerdictBucket = z.infer<typeof PlanningVerdictBucket>;

export const PlanningVerdictReason = z.enum([
  'incomplete_economics',
  'check_eligibility',
  'no_listing',
  'amazon_sells',
  'loses_money',
  'below_floor',
  'not_eligible',
  'wrong_listing',
  'too_slow',
  'apply_approval',
  'confirm_pack',
  'slow_seller',
  'price_spike',
  'price_unstable',
  'check_match',
  'ok',
]);
export type PlanningVerdictReason = z.infer<typeof PlanningVerdictReason>;

export const PlanningResearchVerdict = z.object({
  bucket: PlanningVerdictBucket,
  reason: PlanningVerdictReason,
});
export type PlanningResearchVerdict = z.infer<typeof PlanningResearchVerdict>;

/** `.default(...)` covers a row researched before this shipped — reports the
 *  same "no listing" outcome deriveResearchVerdict would compute for a row
 *  with no ASIN, same defaulting convention as `gating`/`pack`/`amazonRetail`
 *  above. */
const DEFAULT_VERDICT: PlanningResearchVerdict = { bucket: 'no', reason: 'no_listing' };

// ─── Manual sellable override (2026-09-17 design, Faraz: "I need a way to
// mark a product sellable in planning or research page. Once I check
// manually, I should be able to remove the restrictions.") ────────────────
//
// The tenant's current sourcing_sellable_overrides row for this row's
// resolved ASIN, read live the same way `pack.override` reads
// sourcing_pack_overrides — null when none is set. Its existence means the
// tenant has personally confirmed the ASIN is sellable; see
// research-verdict.ts for how it changes bucketing (not_eligible/gated no
// longer force the `no` bucket). Never touches Amazon's own stored gating
// verdict, which stays visible separately under `gating`.
export const PlanningResearchSellableOverride = z.object({
  note: z.string().nullable(),
  updatedAt: z.string(),
});
export type PlanningResearchSellableOverride = z.infer<typeof PlanningResearchSellableOverride>;

// Verified fee override (2026-09-30) — the tenant's saved row from
// sourcing_fee_overrides, null when none is set. Reported even when a real
// Amazon fee quote wins over it (the box shows what was typed in).
// referralFeePct is a fraction (0.15 = 15%).
export const PlanningResearchFeeOverride = z.object({
  fulfillmentFeeCents: z.number().int(),
  referralFeePct: z.number(),
  note: z.string().nullable(),
  updatedAt: z.string(),
});
export type PlanningResearchFeeOverride = z.infer<typeof PlanningResearchFeeOverride>;

export const PlanningResearchRow = z.object({
  candidateId: Uuid,
  key: z.string(),
  source: PlanningCandidateSource,
  status: PlanningCandidateStatus,
  description: z.string().nullable(),
  category: z.string().nullable(),
  asin: z.string().nullable(),
  upc: z.string().nullable(),
  itemNo: z.string().nullable(),
  supplierId: Uuid.nullable(),
  reason: z.string().nullable(),
  tier: PlanningResearchTier.nullable(),
  researchNote: z.string().nullable().default(null),
  anchorPriceCents: z.number().int().nullable(),
  anchorSource: PlanningResearchAnchorSource.nullable(),
  /** Set exactly when anchorSource is 'lowest_fba_offer' (2026-09-11
   *  anchor-spread design) — null otherwise, including for a row researched
   *  before this field existed. See PlanningResearchAnchorSpread's doc. */
  anchorSpread: PlanningResearchAnchorSpread.nullable().default(null),
  lowestOfferShippingCents: z.number().int().nullable(),
  costCents: z.number().int().nullable(),
  estFeesCents: z.number().int().nullable(),
  /** MINOR (2026-09-25, Task C follow-up) — lets the Research row show an
   *  estimated-fee marker (same values OrderPlanLineResearch.feesSource
   *  already carries). `.nullable().default(null)` covers a row researched
   *  before this field existed. */
  feesSource: z
    .enum(['amazon', 'estimated', 'keepa_estimate', 'floor_estimate', 'manual'])
    .nullable()
    .default(null),
  estMarginCents: z.number().int().nullable(),
  marginPct: z.number().nullable(),
  unitsPerListing: z.number().int().nullable(),
  offerCount: z.number().int().nullable(),
  buyboxIsYours: z.boolean().nullable(),
  salesRank: z.number().int().nullable(),
  checkedAt: z.string().nullable(),
  /** anchorPriceCents >= 4x costCents when both are known positive numbers
   *  — a UPC that resolved to a multipack ASIN while the supplier cost is
   *  per-unit produces exactly this signature (Faraz 2026-09-06). Surfaced
   *  as a flag, never hidden or excluded — the row may still be correct. */
  packMismatchSuspected: z.boolean(),
  proposalQuantity: z.number().int().nullable(),
  /** `.default(...)` covers `result.gating` being absent — every row
   *  researched before the gating sweep reaches it, or ever, when SP-API
   *  isn't connected — reports 'unchecked' rather than failing to parse. */
  gating: PlanningResearchGating.default(UNCHECKED_GATING),
  /** `.default(...)` covers a client parsing a response from before this
   *  feature shipped — reports 'unverified' rather than failing to parse,
   *  same convention as `gating` above. */
  amazon: PlanningResearchAmazon.default(UNVERIFIED_AMAZON),
  /** Pack size verdict (Faraz 2026-09-07) — see PlanningResearchPack's doc
   *  above for the default-when-absent convention. */
  pack: PlanningResearchPack.default(DEFAULT_PACK),
  /** Discount-buckets viability (Faraz 2026-09-07) — see
   *  PlanningResearchDiscount's doc above. */
  discount: PlanningResearchDiscount.default(DEFAULT_DISCOUNT),
  /** Velocity-floor verdict (Faraz 2026-09-08) — see
   *  PlanningResearchVelocity's doc above. */
  velocity: PlanningResearchVelocity.default(DEFAULT_VELOCITY),
  /** 90-day Buy Box price context (Faraz 2026-09-10) — see
   *  PlanningResearchPriceContext's doc above. */
  priceContext: PlanningResearchPriceContext.default(DEFAULT_PRICE_CONTEXT),
  /** Row-level warning flags (Faraz 2026-09-10) — see PlanningResearchFlag's
   *  doc above. `.default([])` covers a row researched before this
   *  shipped. */
  flags: z.array(PlanningResearchFlag).default([]),
  /** Amazon-retail exclusion verdict (Faraz 2026-09-10) — see the doc above
   *  DEFAULT_AMAZON_RETAIL. `.default(...)` covers a row researched before
   *  this shipped. */
  amazonRetail: PlanningAmazonRetailVerdict.default(DEFAULT_AMAZON_RETAIL),
  /** Research-verdict simplification (2026-09-11) — see PlanningResearchVerdict's
   *  doc above. `.default(...)` covers a row researched before this shipped. */
  verdict: PlanningResearchVerdict.default(DEFAULT_VERDICT),
  /** Manual sellable override (2026-09-17) — see PlanningResearchSellableOverride's
   *  doc above. `.default(null)` covers a row researched before this shipped
   *  or with no override set. */
  sellableOverride: PlanningResearchSellableOverride.nullable().default(null),
  /** Referral rate (fraction, 0.15 = 15%) the row's fee was rated at, from
   *  research.feesBreakdown.referralFeePct — lets the Verified fee box
   *  prefill its Referral input. Null when the row has no fee breakdown. */
  feesReferralPct: z.number().nullable().default(null),
  /** Verified fee override (2026-09-30) — see PlanningResearchFeeOverride's
   *  doc above. `.default(null)` covers a row researched before this shipped. */
  feeOverride: PlanningResearchFeeOverride.nullable().default(null),
});
export type PlanningResearchRow = z.infer<typeof PlanningResearchRow>;

export const PlanningResearchTierCounts = z.object({
  optimal: z.number().int().nonnegative(),
  'below-target': z.number().int().nonnegative(),
  'below-floor': z.number().int().nonnegative(),
  underwater: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
  /** Sum of the five tier counts above — every row with a research verdict. */
  researched: z.number().int().nonnegative(),
  /** optimal + below-target — the two tiers order planning would act on. */
  buyable: z.number().int().nonnegative(),
});
export type PlanningResearchTierCounts = z.infer<typeof PlanningResearchTierCounts>;

/** Per-status counts over the WHOLE run's researched rows (same
 *  whole-run-not-filtered scope as PlanningResearchTierCounts above) — every
 *  PlanningGatingStatus key always present, same fixed-shape convention as
 *  PlanningResearchTierCounts. */
export const PlanningResearchGatingCounts = z.object({
  open: z.number().int().nonnegative(),
  approval_required: z.number().int().nonnegative(),
  not_eligible: z.number().int().nonnegative(),
  asin_not_found: z.number().int().nonnegative(),
  gated: z.number().int().nonnegative(),
  unchecked: z.number().int().nonnegative(),
});
export type PlanningResearchGatingCounts = z.infer<typeof PlanningResearchGatingCounts>;

/** Per-identity-status counts over the WHOLE run's researched rows — same
 *  whole-run-not-filtered scope and fixed-shape convention as
 *  PlanningResearchTierCounts/PlanningResearchGatingCounts above. */
export const PlanningResearchIdentityCounts = z.object({
  match: z.number().int().nonnegative(),
  mismatch: z.number().int().nonnegative(),
  unverified: z.number().int().nonnegative(),
});
export type PlanningResearchIdentityCounts = z.infer<typeof PlanningResearchIdentityCounts>;

/** Per-pack-confidence counts over the WHOLE run's researched rows (Faraz
 *  2026-09-07) — same whole-run-not-filtered scope and fixed-shape
 *  convention as PlanningResearchIdentityCounts above. `unverified` names
 *  the `assumed_single` confidence value (research's own ×1 fallback), same
 *  renaming the `pack` query filter itself does. */
export const PlanningResearchPackCounts = z.object({
  known: z.number().int().nonnegative(),
  unclear: z.number().int().nonnegative(),
  unverified: z.number().int().nonnegative(),
  /** Pack override design (2026-09-08) — count of researched rows with
   *  `pack.suspect` true, independent of the confidence buckets above (a
   *  suspect row can be known/unclear/unverified). */
  suspect: z.number().int().nonnegative(),
});
export type PlanningResearchPackCounts = z.infer<typeof PlanningResearchPackCounts>;

/** Whole-run counts of researched rows that clear their OWN floor threshold
 *  at each discount bucket (Faraz 2026-09-07 — see PlanningResearchDiscount's
 *  doc above), same whole-run-not-filtered scope and fixed-shape convention
 *  as the other counts objects here. Buckets 0 (list) isn't included: it's
 *  already covered by `buyable` above. */
export const PlanningResearchBuyableAtCounts = z.object({
  '5': z.number().int().nonnegative(),
  '10': z.number().int().nonnegative(),
  '15': z.number().int().nonnegative(),
});
export type PlanningResearchBuyableAtCounts = z.infer<typeof PlanningResearchBuyableAtCounts>;

/** Per-velocity-tier counts over the WHOLE run's researched rows (Faraz
 *  2026-09-08 velocity-floor design), same whole-run-not-filtered scope and
 *  fixed-shape convention as the other counts objects here. */
export const PlanningResearchVelocityCounts = z.object({
  pass: z.number().int().nonnegative(),
  marginal: z.number().int().nonnegative(),
  fail: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
});
export type PlanningResearchVelocityCounts = z.infer<typeof PlanningResearchVelocityCounts>;

/** Per-amazon-retail-status counts over the WHOLE run's researched rows
 *  (Faraz 2026-09-10 amazon-retail-exclusion design), same whole-run-not-
 *  filtered scope and fixed-shape convention as the other counts objects
 *  here. Drives the Flags filter's amazon option and the strip's
 *  "Amazon sells N" line. */
export const PlanningResearchAmazonRetailCounts = z.object({
  amazon: z.number().int().nonnegative(),
  clear: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
});
export type PlanningResearchAmazonRetailCounts = z.infer<typeof PlanningResearchAmazonRetailCounts>;

/** Per-bucket counts over the WHOLE run's researched rows (research-verdict
 *  simplification, 2026-09-11), same whole-run-not-filtered scope and
 *  fixed-shape convention as PlanningResearchAmazonRetailCounts above. */
export const PlanningResearchVerdictCounts = z.object({
  buy: z.number().int().nonnegative(),
  fix: z.number().int().nonnegative(),
  no: z.number().int().nonnegative(),
});
export type PlanningResearchVerdictCounts = z.infer<typeof PlanningResearchVerdictCounts>;

// "Split the list" (Faraz 2026-09-26) — the Buy tab splits verdict='buy' rows
// by Amazon sellability rather than surfacing gating as a peer filter: `now`
// is buy+gating='open' (sell it today). Gating carried across nightly runs
// (2026-09-28 tenant-level ASIN cache design) means "never checked" is no
// longer rare enough to fold into "after approval" — `afterApproval` is now
// buy+gating NOT IN ('open','unchecked') (approval_required or another real
// gated verdict), and `unchecked` is its own group: buy+gating='unchecked'.
// Verdict logic itself is unchanged — all three groups still carry verdict
// 'buy'; this is purely a whole-run-scoped split of that one bucket, same
// fixed-shape/default convention as the other counts objects above.
export const PlanningResearchBuySplitCounts = z.object({
  now: z.number().int().nonnegative(),
  afterApproval: z.number().int().nonnegative(),
  unchecked: z.number().int().nonnegative(),
});
export type PlanningResearchBuySplitCounts = z.infer<typeof PlanningResearchBuySplitCounts>;

// 2026-09-17 provider-queues design, Task 6 ("Research UI and observability"
// — docs/plans/2026-09-17-research-provider-queues-design.md). Additive
// counters over `research_provider_jobs` for one run, kept deliberately
// separate from the tier/gating/verdict counts above: provider progress is
// scheduling state, never a business verdict (Buy/Fix first/No business).
// Amazon's compact summary: completed/total, active (currently leased),
// waiting (pending or waiting on capacity), failed (unavailable or failed).
export const PlanningProviderCounts = z.object({
  total: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  waiting: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type PlanningProviderCounts = z.infer<typeof PlanningProviderCounts>;
// Keepa's compact summary uses different buckets (design doc: "Keepa:
// completed / total, reused, waiting for tokens, unavailable/failed") — a
// dedicated 429/cooldown wait is operator-meaningful in a way Amazon's
// generic `waiting` isn't. `reused` is carried as a real field for forward
// compatibility with the reuse-across-runs behavior the design describes;
// no job-creation path populates it yet (see provider-progress.ts's doc),
// so it currently always reads 0 — never fabricated as nonzero.
export const PlanningKeepaProviderCounts = z.object({
  total: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  reused: z.number().int().nonnegative(),
  waitingForTokens: z.number().int().nonnegative(),
  unavailableOrFailed: z.number().int().nonnegative(),
});
export type PlanningKeepaProviderCounts = z.infer<typeof PlanningKeepaProviderCounts>;
// The five states the design's "Research UI and observability" section
// names, distinct from the stored `order_planning_runs.status` enum: this is
// a DISPLAY state derived from preparation/provider-job/projection-backlog
// progress, never a second source of truth for run status.
export const PlanningResearchRunState = z.enum([
  'preparing',
  'researching',
  'enriching',
  'complete',
  'complete_with_gaps',
]);
export type PlanningResearchRunState = z.infer<typeof PlanningResearchRunState>;
export const PlanningProviderProgress = z.object({
  runState: PlanningResearchRunState,
  amazon: PlanningProviderCounts,
  keepa: PlanningKeepaProviderCounts,
  /** Earliest dueAt among either provider's still-pending/waiting jobs for
   *  this run — UTC ISO, null when nothing is waiting. The web formats this
   *  in Central time (Faraz: report times in Central, never UTC). */
  nextEligibleRetryAt: z.string().nullable(),
});
export type PlanningProviderProgress = z.infer<typeof PlanningProviderProgress>;

export const ListPlanningResearchResponse = z.object({
  /** Materialization freshness, independent of the provider research run status. */
  freshness: z
    .object({
      initialized: z.boolean(),
      status: z.enum(['preparing', 'refreshing', 'ready']),
      pendingCount: z.number().int().nonnegative(),
      evidenceRefreshPending: z.boolean(),
      lastMaterializedAt: z.string().nullable(),
    })
    .optional(),
  run: z.object({
    id: Uuid,
    day: z.string(),
    status: z.string(),
    holdReason: z.string().nullable(),
  }),
  rows: z.array(PlanningResearchRow),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  counts: PlanningResearchTierCounts.extend({
    gating: PlanningResearchGatingCounts,
    identity: PlanningResearchIdentityCounts,
    pack: PlanningResearchPackCounts,
    buyableAt: PlanningResearchBuyableAtCounts,
    velocity: PlanningResearchVelocityCounts,
    /** buyable (optimal or below-target) AND velocity tier 'pass' — the
     *  "proven N of buyable M" strip line's numerator (Faraz 2026-09-08:
     *  "buyable itself keeps its margin-only meaning; proven is the new
     *  word for margin plus demand"). */
    buyableProven: z.number().int().nonnegative(),
    /** Whole-run count of researched rows flagged `price_spike` (Faraz
     *  2026-09-10) — same filter-free whole-run scope as the other counts
     *  here. */
    priceSpike: z.number().int().nonnegative(),
    /** Whole-run count of researched rows flagged `price_unstable`
     *  (2026-09-11 anchor-spread design) — same filter-free whole-run scope
     *  as `priceSpike` above. */
    priceUnstable: z.number().int().nonnegative(),
    /** Amazon-retail-exclusion design (Faraz 2026-09-10) — see
     *  PlanningResearchAmazonRetailCounts' doc above. */
    amazonRetail: PlanningResearchAmazonRetailCounts,
    /** Research-verdict simplification (2026-09-11) — see
     *  PlanningResearchVerdictCounts' doc above. */
    verdict: PlanningResearchVerdictCounts,
    /** "Split the list" (2026-09-26) — see PlanningResearchBuySplitCounts'
     *  doc above. `.default(...)` covers a response captured before this
     *  shipped. */
    buySplit: PlanningResearchBuySplitCounts.default({ now: 0, afterApproval: 0, unchecked: 0 }),
    /** Candidates deferred before a complete pricing verdict was possible. */
    incomplete: z.number().int().nonnegative().optional(),
    /** Whole-run count of researched rows per verdict reason code (2026-09-11
     *  design) — every PlanningVerdictReason key that occurred at least once;
     *  a code with zero rows is simply absent (unlike the fixed-shape counts
     *  objects above) since the reason space is 13 codes wide and most runs
     *  won't hit all of them. */
    reason: z.record(z.string(), z.number().int().nonnegative()),
  }),
  /** 2026-09-17 provider-queues Task 6 — additive, optional so a response
   *  captured before this shipped (or a legacy execution-version-1 run,
   *  which carries no research_provider_jobs rows at all) still parses. */
  providerProgress: PlanningProviderProgress.optional(),
});
export type ListPlanningResearchResponse = z.infer<typeof ListPlanningResearchResponse>;
